import { useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { WizardSubmissionRow } from "@/lib/supabase"
import {
  getProgrammeIdFromWizard,
  getNextSessionNumber,
  getProgramMeta,
  getProgramSession,
  sessionDisplayName,
} from "@/lib/programs"

export interface NextProgramSession {
  nextSessionNumber: number | null
  nextSessionLabel: string | null
  nextSessionDate: string | null   // event_date du calendar_events pour nextSessionNumber
  nextSessionTime: string | null   // end_time du même événement
  loaded: boolean                  // true une fois la résolution terminée (succès ou échec)
}

export function useNextProgramSession(): NextProgramSession {
  const [state, setState] = useState<NextProgramSession>({
    nextSessionNumber: null,
    nextSessionLabel: null,
    nextSessionDate: null,
    nextSessionTime: null,
    loaded: false,
  })

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user || cancelled) return

        const { data: wizard } = await supabase
          .from("wizard_submissions")
          .select("level_id, goal_id, place, gender")
          .eq("user_id", user.id)
          .maybeSingle()

        if (!wizard || cancelled) return

        const programmeId = getProgrammeIdFromWizard(wizard as WizardSubmissionRow)
        const meta = await getProgramMeta(programmeId)
        if (!meta || cancelled) return

        const next = await getNextSessionNumber(user.id, meta.sessions_total, programmeId)
        if (cancelled) return

        // Fetch session label and calendar event date in parallel
        const [session, calResult] = await Promise.all([
          getProgramSession(programmeId, next),
          supabase
            .from("calendar_events")
            .select("event_date, end_time")
            .eq("user_id", user.id)
            .eq("type", "seance-planned")
            .eq("programme_session_number", next)
            .maybeSingle(),
        ])
        if (cancelled) return

        // Même règle que le calendrier et que l'écran de séance : le TYPE de la
        // séance. Ce hook recopiait auparavant, à la main, l'extraction depuis le
        // libellé — c'est ce qui faisait annoncer à l'accueil et au calendrier
        // deux noms différents pour la même séance.
        const label = session ? sessionDisplayName(session.type, next) : null

        const calRow = calResult.data as { event_date: string; end_time: string | null } | null

        setState({
          nextSessionNumber: next,
          nextSessionLabel: label,
          nextSessionDate: calRow?.event_date ?? null,
          nextSessionTime: calRow?.end_time ?? null,
          loaded: true,
        })
      } catch {
        if (!cancelled) setState(s => ({ ...s, loaded: true }))
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  return state
}
